import React from 'react';
import ApiClient from "../../utils/ApiClient";

export default function OrderVerificationModal({
                                                   isOpen,
                                                   onClose,
                                                   currentOrder,
                                                   onOrderShipped
                                               }) {
    const [scanValue, setScanValue] = React.useState('');
    const [verificationError, setVerificationError] = React.useState('');
    const [processingVerification, setProcessingVerification] = React.useState(false);
    const [verificationStatus, setVerificationStatus] = React.useState(null);

    // Reset state when modal opens
    React.useEffect(() => {
        if (isOpen) {
            setScanValue('');
            setVerificationError('');
            setVerificationStatus(null);
            setProcessingVerification(false);

            // Auto-focus input
            const input = document.getElementById('scan-input');
            if (input) {
                input.focus();
            }
        }
    }, [isOpen]);

    const handleVerifyProduct = async (e) => {
        e.preventDefault();
        if (!scanValue.trim()) return;

        setProcessingVerification(true);
        setVerificationError('');

        console.log('Current order data:', currentOrder);

        try {
            const response = await ApiClient.post('/api/orders/productverification', {
                orderId: currentOrder.id,
                scanValue: scanValue.trim()
            });

            console.log('Verification response:', response.data);
            console.log('Response type:', typeof response.data);
            console.log('Scanned value:', scanValue.trim());
            console.log('Order ID:', currentOrder.id);

            if (response.data === 1 || response.data === 2) {
                setVerificationStatus(response.data);
            } else if (response.data === 0) {
                setVerificationError('Product not found. The scanned FNSKU, SKU, or UPC does not match this order item.');
            } else {
                setVerificationError('Unexpected response from server. Please try again.');
            }
        } catch (error) {
            setVerificationError('Error verifying product. Please try again.');
            console.error('Error during verification:', error);
        } finally {
            setProcessingVerification(false);
        }
    };

    const handleAcceptAndUpdate = async () => {
        setProcessingVerification(true);
        try {
            // Log the current order object for debugging
            console.log('Current order in modal before update:', currentOrder);

            // Make the API call to update local inventory
            await ApiClient.post('/api/orders/updatelocalinventory', {
                locationId: currentOrder.calibrate_local_inventory_location_id,
            });

            // Create result object with sold explicitly set to 1
            const result = {
                sold: 1,
                verificationStatus: verificationStatus,
                order_status: 'Shipped',
                id: currentOrder.id
            };

            // Log the result we're sending back
            console.log('Sending result back to parent:', result);

            // Close the modal first to avoid any state interference
            onClose();

            // Then call the onOrderShipped with a slight delay to ensure the modal is fully closed
            setTimeout(() => {
                // Key on order_location_id (the link-table row id) — that's what the
                // orders page matches on. currentOrder.id is amazon_orders.id and would
                // match no row, leaving the UI unchanged after Accept.
                onOrderShipped(currentOrder.order_location_id, result);
            }, 10);
        } catch (error) {
            setVerificationError('Error updating inventory. Please try again.');
            console.error('Error during inventory update:', error);
        } finally {
            setProcessingVerification(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
            <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"></div>

            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                    <div>
                        <h3 className="text-lg font-semibold text-center text-gray-900">
                            Verify Product
                        </h3>

                        {currentOrder && (
                            <div className="mt-4 bg-gray-50 p-4 rounded-md">
                                <h4 className="font-medium text-gray-900">Product Details:</h4>
                                <div className="mt-2 space-y-2 text-sm">
                                    <p className="text-gray-700">
                                        <span className="font-medium">Product:</span>{' '}
                                        {currentOrder.product_name}
                                    </p>
                                    <div className="flex flex-col gap-1">
                                        <p className="text-gray-700">
                                            <span className="font-medium">FNSKU:</span>{' '}
                                            {currentOrder.identifier || 'Not set'}
                                        </p>
                                        <p className="text-gray-700">
                                            <span className="font-medium">local ID:</span>{' '}
                                            {currentOrder.calibrate_local_inventory_location_id }
                                        </p>
                                        <p className="text-gray-700">
                                            <span className="font-medium">SKU:</span>{' '}
                                            {currentOrder.sku}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        <form onSubmit={handleVerifyProduct} className="mt-4">
                            <div>
                                <label htmlFor="scan-input" className="block text-sm font-medium text-gray-700">
                                    Scan FNSKU or UPC
                                </label>
                                <input
                                    type="text"
                                    id="scan-input"
                                    value={scanValue}
                                    onChange={(e) => setScanValue(e.target.value)}
                                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                    placeholder="Scan product code"
                                    autoComplete="off"
                                    disabled={verificationStatus !== null}
                                />
                            </div>

                            {verificationError && (
                                <div className="mt-2 rounded-md bg-red-50 p-4">
                                    <div className="text-sm text-red-700">
                                        {verificationError}
                                    </div>
                                </div>
                            )}

                            {verificationStatus === 1 && (
                                <div className="mt-2 rounded-md bg-yellow-50 p-4">
                                    <div className="text-sm text-yellow-800">
                                        <p className="mb-2">
                                            This will remove 1 unit from local inventory. Please confirm.
                                        </p>
                                        <div className="mt-3 flex gap-3">
                                            <button
                                                type="button"
                                                onClick={handleAcceptAndUpdate}
                                                disabled={processingVerification}
                                                className="inline-flex justify-center rounded-md border border-transparent bg-yellow-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 disabled:bg-yellow-300 disabled:cursor-not-allowed"
                                            >
                                                Accept
                                            </button>
                                            <button
                                                type="button"
                                                onClick={onClose}
                                                className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {verificationStatus === 2 && currentOrder && (
                                <div className="mt-2 rounded-md bg-yellow-50 p-4">
                                    <div className="text-sm text-yellow-800">
                                        <p className="mb-2">
                                            A UPC/EAN/GTIN was used to identify the product. Typically this is done for New condition units only and this product's condition is {currentOrder.display_condition}.
                                        </p>
                                        <div className="mt-3 flex gap-3">
                                            <button
                                                type="button"
                                                onClick={handleAcceptAndUpdate}
                                                disabled={processingVerification}
                                                className="inline-flex justify-center rounded-md border border-transparent bg-yellow-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 disabled:bg-yellow-300 disabled:cursor-not-allowed"
                                            >
                                                Accept
                                            </button>
                                            <button
                                                type="button"
                                                onClick={onClose}
                                                className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {!verificationStatus && (
                                <div className="mt-5 sm:mt-6 flex flex-row-reverse gap-3">
                                    <button
                                        id="verify-product-btn"
                                        type="submit"
                                        disabled={processingVerification || !scanValue.trim()}
                                        className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:bg-gray-300 disabled:cursor-not-allowed"
                                    >
                                        {processingVerification ? 'Verifying...' : 'Verify Product'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            )}
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}